import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import { Box, Typography, MenuItem, Button, Divider, Stack, Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useFormsFlags from '../hooks/useFormsFlags';
import { Iconify } from '.';
import { PATH_DASHBOARD } from '../routes/paths';

const RED = '#B72136';

const getOptions = ({ v1Enabled, v2Enabled }) => [
    {
        label: 'Feature Flags',
        value: 'super_admin_feature_flags',
        linkTo: PATH_DASHBOARD.superAdmin.featureFlags,
        icon: 'solar:flag-bold-duotone',
    },
    {
        label: 'Create Practice',
        value: 'super_admin_create_practice',
        linkTo: PATH_DASHBOARD.superAdmin.createPractice,
        icon: 'solar:buildings-2-bold-duotone',
    },
    {
        label: 'User Management',
        value: 'super_admin_user_management',
        linkTo: PATH_DASHBOARD.superAdmin.UserManagement,
        icon: 'solar:users-group-two-rounded-bold-duotone',
    },
    {
        label: 'Tasks V2 Migration',
        value: 'super_admin_tasks_v2_migration',
        linkTo: PATH_DASHBOARD.superAdmin.tasksV2Migration,
        icon: 'solar:clipboard-check-bold-duotone',
    },
    {
        label: 'Practice Choices',
        value: 'super_admin_practice_choices',
        linkTo: PATH_DASHBOARD.superAdmin.PracticeChoices,
        icon: 'solar:list-check-bold-duotone',
    },
    ...(v1Enabled ? [{
        label: 'Consent',
        value: 'super_admin_consent',
        linkTo: PATH_DASHBOARD.superAdmin.consent,
        icon: 'solar:document-add-bold-duotone',
    }] : []),
    ...(v2Enabled ? [{
        label: 'Form Builder',
        value: 'super_admin_consent_builder',
        linkTo: PATH_DASHBOARD.superAdmin.consentV2,
        icon: 'solar:document-text-bold-duotone',
    }] : []),
    {
        label: 'Auto Reminder',
        value: 'super_admin_auto_reminder',
        linkTo: PATH_DASHBOARD.superAdmin.BulkAutoReminders,
        icon: 'solar:alarm-bold-duotone',
    },
    {
        label: 'Import Leads',
        value: 'super_admin_import_leads',
        linkTo: PATH_DASHBOARD.superAdmin.ImportLeads,
        icon: 'solar:upload-minimalistic-bold-duotone',
    },
    {
        label: 'Engagement Templates',
        value: 'super_admin_engagement',
        linkTo: PATH_DASHBOARD.superAdmin.engagementTemplates,
        icon: 'solar:letter-bold-duotone',
    },
    { divider: true, label: 'Groups' },
    {
        label: 'Group Dashboard',
        value: 'super_admin_group_dashboard',
        linkTo: PATH_DASHBOARD.superAdmin.groupDashboard,
        icon: 'solar:buildings-3-bold-duotone',
    },
    { divider: true, label: 'Practice Defaults' },
    {
        label: 'Table Defaults',
        value: 'super_admin_table_defaults',
        linkTo: PATH_DASHBOARD.superAdmin.tableDefaults,
        icon: 'solar:widget-2-bold-duotone',
    },
    {
        label: 'Table Defaults — V2',
        value: 'super_admin_table_defaults_preview',
        linkTo: PATH_DASHBOARD.superAdmin.tableDefaultsPreview,
        icon: 'solar:test-tube-bold-duotone',
        badge: 'V2',
    },
    {
        label: 'Sidebar Navigation',
        value: 'super_admin_sidebar_navigation',
        linkTo: PATH_DASHBOARD.superAdmin.sidebarNavigation,
        icon: 'solar:sidebar-minimalistic-bold-duotone',
    },
    { divider: true, label: 'Tasks & Notifications' },
    {
        label: 'System Task Templates',
        value: 'super_admin_system_task_templates',
        linkTo: PATH_DASHBOARD.superAdmin.systemTaskTemplates,
        icon: 'solar:checklist-minimalistic-bold-duotone',
    },
    {
        label: 'Notification Management',
        value: 'super_admin_notification_management',
        linkTo: PATH_DASHBOARD.superAdmin.notificationManagement,
        icon: 'solar:bell-bing-bold-duotone',
    },
];

const SuperAdminMenu = ({ selected }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { v1Enabled, v2Enabled } = useFormsFlags();

    const options = getOptions({ v1Enabled, v2Enabled });

    return (
        <Box sx={{ p: 2, pt: 3, '@media(max-width: 1200px)': { display: 'none' } }}>
            <Button
                sx={{
                    mb: 1.5,
                    ml: 0,
                    minWidth: '200px',
                    width: '100%',
                    bgcolor: RED,
                    '&:hover': { bgcolor: '#8A1628' },
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                }}
                variant="contained"
                onClick={() => navigate(PATH_DASHBOARD.general.app)}
                startIcon={<Iconify icon="solar:arrow-left-bold" />}
            >
                Back to Dashboard
            </Button>

            <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: RED, mb: 1.5, pl: 0.5 }}>
                    Super Admin
                </Typography>
                <Stack spacing={0}>
                    <Divider sx={{ mb: 1 }} />
                    {options.map((option, idx) => {
                        if (option.divider) {
                            return (
                                <Box key={`divider-${idx}`} sx={{ mt: 1.5, mb: 0.5 }}>
                                    <Divider />
                                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mt: 1, mb: 0.5, pl: 0.5 }}>
                                        {option.label}
                                    </Typography>
                                </Box>
                            );
                        }
                        const isSelected = selected === option.value;
                        return (
                            <MenuItem
                                key={option.value}
                                selected={isSelected}
                                to={option.linkTo}
                                component={RouterLink}
                                sx={{
                                    px: 1,
                                    py: 0.75,
                                    mb: '2px',
                                    minHeight: 'auto',
                                    borderRadius: '8px',
                                    '&:hover': { bgcolor: alpha(RED, 0.08) },
                                    '&:hover .sa-sidebar-icon': {
                                        bgcolor: alpha(RED, 0.12),
                                        '& svg': { color: RED },
                                    },
                                    '&.Mui-selected': { bgcolor: alpha(RED, theme.palette.action.selectedOpacity) },
                                    '&.Mui-selected .sa-sidebar-icon': {
                                        bgcolor: alpha(RED, 0.16),
                                        '& svg': { color: RED },
                                    },
                                    '&.Mui-selected .sa-sidebar-label': { color: RED },
                                }}
                            >
                                <Box
                                    className="sa-sidebar-icon"
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: alpha(theme.palette.grey[500], 0.08),
                                        mr: 1.5,
                                        flexShrink: 0,
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    <Iconify
                                        icon={option.icon}
                                        width={18}
                                        sx={{ color: theme.palette.text.secondary, transition: 'color 0.2s' }}
                                    />
                                </Box>
                                <Typography
                                    className="sa-sidebar-label"
                                    sx={{
                                        fontSize: '0.8125rem',
                                        fontWeight: 500,
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.4,
                                        transition: 'color 0.2s',
                                        flex: 1,
                                    }}
                                >
                                    {option.label}
                                    {option.badge && (
                                        <Chip
                                            label={option.badge}
                                            size="small"
                                            sx={{
                                                ml: 0.75,
                                                height: 16,
                                                fontSize: '0.6rem',
                                                fontWeight: 700,
                                                letterSpacing: '0.04em',
                                                bgcolor: alpha('#8B5CF6', 0.12),
                                                color: '#7C3AED',
                                                '& .MuiChip-label': { px: 0.75 },
                                            }}
                                        />
                                    )}
                                </Typography>
                            </MenuItem>
                        );
                    })}
                </Stack>
            </Box>
        </Box>
    );
};

export default SuperAdminMenu;
